import { css } from "../helpers/webComponents";
import { AccountsRouteService } from "../services/AccountsRouteService";
import { IsLoggedInResponse, RegisterLoginResponse } from "@shared/types";
import { PlayerService } from "../services/PlayerService";

/** CSS affecting the {@link AccountComponent} */
const styles: string = css`
    .account {
        position: fixed;
        top: 10px;
        left: 10px;
        padding: 8px;
        cursor: pointer;
        font-family: sans-serif;
        z-index: 1000;
        background-color: #001F54;
        border: 1px solid #001F54;
        border-radius: 10px;
    }
    .dropdown {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        background-color: #001F54;
        border: 1px solid #001F54;
        z-index: 10001;
        white-space: nowrap;
        border-radius: 0 0 10px 10px;
    }
    .account:hover .dropdown {
        display: block;
    }
    .account:hover {
        border-radius: 10px 10px 10px 0;
        background-color: #075398;
    }
    .dropdown button:last-child {
        border-radius: 0 0 10px 10px;
    }
    .dropdown button {
        display: block;
        width: 100%;
        border: none;
        background: transparent;
        padding: 4px;
        text-align: left;
        cursor: pointer;
        color: white;
        font-size: 18px;
    }
    .dropdown button:hover {
        background-color: #075398;
    }

    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10002;
    }
    .modal {
        background-color: #001F54;
        padding: 20px;
        border-radius: 8px;
        width: 300px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    }
    .modal h2 {
        margin-top: 0;
    }
    .modal input {
        width: 100%;
        margin-bottom: 10px;
        padding: 8px;
        box-sizing: border-box;
        background-color: #034078;
        color: white;
        border: 1px solid #001F54;
        border-radius: 4px;
        outline: none;
    }
    .modal button {
        margin-right: 10px;
        padding: 8px 12px;
        background-color: #034078;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
    }
    .modal button:hover {
        background-color: #075398;
    }
`;

export class AccountComponent extends HTMLElement {
    private accountInfo?: IsLoggedInResponse;
    private _playerService: PlayerService;

    public constructor() {
        super();
        this._playerService = new PlayerService();
    }

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.init();
    }

    /**
     * Initialize the component by checking the login status.
     */
    private async init(): Promise<void> {
        const accountsService: AccountsRouteService = new AccountsRouteService();
        try {
            // Check if user is logged in
            this.accountInfo = await accountsService.isLoggedIn();
        }
        catch (error) {
            console.error("Error checking login status:", error);
            this.accountInfo = undefined;
        }
        this.render();
    }

    /**
     * Render the account component.
     * If logged in, shows the user's name and a dropdown for logout.
     * If not logged in, shows "Not Logged In" and a dropdown for login/register.
     */
    public render(): void {
        if (!this.shadowRoot) {
            return;
        }

        // Clear any previous content in the shadow DOM.
        this.shadowRoot.innerHTML = "";

        // Create and append style.
        const styleElem: HTMLStyleElement = document.createElement("style");
        styleElem.textContent = styles;
        this.shadowRoot.appendChild(styleElem);

        // Main container for account info.
        const container: HTMLDivElement = document.createElement("div");
        container.classList.add("account");

        if (this.accountInfo) {
            // User is logged in, show user's name.
            const nameSpan: HTMLSpanElement = document.createElement("span");
            nameSpan.textContent = this.accountInfo.name;
            container.appendChild(nameSpan);

            // Dropdown with a logout button.
            const dropdown: HTMLDivElement = document.createElement("div");
            dropdown.classList.add("dropdown");

            const logoutButton: HTMLButtonElement = document.createElement("button");
            logoutButton.textContent = "Logout";
            logoutButton.addEventListener("click", () => {
                localStorage.removeItem("playerSessionId");
                // Refresh page
                window.location.reload();
            });
            dropdown.appendChild(logoutButton);
            container.appendChild(dropdown);
        }
        else {
            // User is not logged in, show "Not Logged In".
            const notLoggedSpan: HTMLSpanElement = document.createElement("span");
            notLoggedSpan.textContent = "Not Logged In";
            container.appendChild(notLoggedSpan);

            // Dropdown with login/register buttons.
            const dropdown: HTMLDivElement = document.createElement("div");
            dropdown.classList.add("dropdown");

            const loginButton: HTMLButtonElement = document.createElement("button");
            loginButton.textContent = "Login";
            loginButton.addEventListener("click", () => {
                this.openOverlay("login");
            });
            dropdown.appendChild(loginButton);

            const registerButton: HTMLButtonElement = document.createElement("button");
            registerButton.textContent = "Register";
            registerButton.addEventListener("click", () => {
                this.openOverlay("register");
            });
            dropdown.appendChild(registerButton);

            container.appendChild(dropdown);
        }

        this.shadowRoot.appendChild(container);
    }

    /**
     * Opens an overlay modal with a form.
     * @param mode "login" to display login fields or "register" to display registration fields.
     */
    private openOverlay(mode: "login" | "register"): void {
        if (!this.shadowRoot) return;

        // Create overlay element.
        const overlay: HTMLDivElement = document.createElement("div");
        overlay.classList.add("overlay");

        // Create modal container.
        const modal: HTMLDivElement = document.createElement("div");
        modal.classList.add("modal");

        const title: HTMLHeadingElement = document.createElement("h2");
        title.textContent = mode === "login" ? "Login" : "Register";
        modal.appendChild(title);

        // Create form.
        const form: HTMLFormElement = document.createElement("form");

        const usernameInput: HTMLInputElement = document.createElement("input");
        usernameInput.type = "text";
        usernameInput.placeholder = "Username";
        usernameInput.required = true;
        form.appendChild(usernameInput);

        const passwordInput: HTMLInputElement = document.createElement("input");
        passwordInput.type = "password";
        passwordInput.placeholder = "Password";
        passwordInput.required = true;
        form.appendChild(passwordInput);

        let confirmPasswordInput: HTMLInputElement | null = null;
        if (mode === "register") {
            confirmPasswordInput = document.createElement("input");
            confirmPasswordInput.type = "password";
            confirmPasswordInput.placeholder = "Confirm Password";
            confirmPasswordInput.required = true;
            form.appendChild(confirmPasswordInput);
        }

        const submitButton: HTMLButtonElement = document.createElement("button");
        submitButton.type = "submit";
        submitButton.textContent = mode === "login" ? "Login" : "Register";
        form.appendChild(submitButton);

        const cancelButton: HTMLButtonElement = document.createElement("button");
        cancelButton.type = "button";
        cancelButton.textContent = "Cancel";
        cancelButton.addEventListener("click", () => {
            this.shadowRoot?.removeChild(overlay);
        });
        form.appendChild(cancelButton);

        form.addEventListener("submit", async event => {
            event.preventDefault();
            const username: string = usernameInput.value;
            const password: string = passwordInput.value;

            if (mode === "register" && confirmPasswordInput) {
                if (password !== confirmPasswordInput.value) {
                    alert("Passwords do not match.");
                    return;
                }
            }

            const accountsService: AccountsRouteService = new AccountsRouteService();

            try {
                if (mode === "login") {
                    // Call login and then set player session id in localStorage
                    const response: RegisterLoginResponse = await accountsService.login(username, password);
                    localStorage.setItem("playerSessionId", response.id_save);
                    // alert("Login successful!");
                    // this.shadowRoot?.removeChild(overlay);
                    window.location.reload();
                }
                else {
                    // For registration, get session id from player service and call register
                    const sessionId: string = this._playerService.getPlayerSessionId();
                    await accountsService.register(username, password, sessionId);
                    // alert("Registration successful!");
                    // this.shadowRoot?.removeChild(overlay);
                    window.location.reload();
                }
                // Refresh component state (maybe not unnecessary now?)
                await this.init();
            }
            catch (error) {
                console.error(`${mode} error:`, error);
                alert(`${mode.charAt(0).toUpperCase() + mode.slice(1)} failed.`);
            }
        });

        modal.appendChild(form);
        overlay.appendChild(modal);
        this.shadowRoot.appendChild(overlay);
    }
}
